import { useEffect } from "react";
import { useDispatch } from "react-redux";

import { setCookie } from "lib/cookies";
import useCurrentTheme from "store/ui/uiSelector";
import { uiActions } from "store/ui/uiSlice";

import { ThemeType } from "../types";

function disableTransitionsTemporarily() {
  document.documentElement.classList.add("**:transition-none!");
  window.setTimeout(() => {
    document.documentElement.classList.remove("**:transition-none!");
  }, 0);
}

interface UseThemeType {
  theme: ThemeType;
  setTheme: (newTheme: ThemeType) => void;
  toggleTheme: () => void;
}

const useTheme = (): UseThemeType => {
  const theme = useCurrentTheme();
  const dispatch = useDispatch();

  function toggleTheme() {
    disableTransitionsTemporarily();
    const isDarkMode = document.documentElement.classList.toggle("dark");
    const newTheme = isDarkMode ? "dark" : "light";
    // We are setting a date because without it, the cookie will be a session cookie.
    // Session cookies are treated differently by different browsers and likely not
    // persisted across browser restarts.
    setCookie("theme", newTheme, 365);
    dispatch(uiActions.setTheme(newTheme));
  }

  function setTheme(newTheme: ThemeType) {
    disableTransitionsTemporarily();
    if (newTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    setCookie("theme", newTheme, 365);
    dispatch(uiActions.setTheme(newTheme));
  }

  useEffect(() => {
    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      document.documentElement.classList.add(
        mediaQuery.matches ? "dark" : "light",
      );

      const handleChange = (e: MediaQueryListEvent) => {
        document.documentElement.classList.remove("dark", "light");

        if (e.matches) {
          document.documentElement.classList.add(
            mediaQuery.matches ? "dark" : "light",
          );
        }
      };

      mediaQuery.addEventListener("change", handleChange);

      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [theme]);

  return {
    theme: theme,
    setTheme,
    toggleTheme,
  };
};

export default useTheme;
